import { Controller } from '@hotwired/stimulus'
import { Chart, registerables } from 'chart.js'

Chart.register(...registerables)

export default class extends Controller {
  static targets = ['canvas']
  static values = {
    type: { type: String, default: 'line' },
    data: Object,
    options: { type: Object, default: {} },
    showPercentage: { type: Boolean, default: false },
  }

  connect() {
    this.initializeChart()
  }

  disconnect() {
    this.destroyChart()
  }

  initializeChart() {
    if (!this.hasCanvasTarget) {
      console.error('Chart controller: canvas target not found')
      return
    }

    this.destroyChart()

    const options = this.optionsValue

    if (this.showPercentageValue) {
      options.plugins = options.plugins || {}
      options.plugins.tooltip = options.plugins.tooltip || {}
      options.plugins.tooltip.callbacks = {
        label: (context) => {
          const total = context.dataset.data.reduce((sum, val) => sum + val, 0)
          const value = context.dataset.data[context.dataIndex]
          const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0
          return ` ${context.label}: ${percentage}%`
        }
      }
    }

    const config = {
      type: this.typeValue,
      data: this.dataValue,
      options,
    }

    this.chart = new Chart(this.canvasTarget, config)
  }

  destroyChart() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  }

  updateData(newData) {
    if (this.chart) {
      this.chart.data = newData
      this.chart.update()
    }
  }

  typeValueChanged() {
    this.initializeChart()
  }

  dataValueChanged() {
    if (this.chart) {
      this.chart.data = this.dataValue
      this.chart.update()
    }
  }

  optionsValueChanged() {
    this.initializeChart()
  }

  toggleDataset(event) {
    if (!this.chart) return

    // currentTarget, not target: on non-checkbox toggles (e.g. clickable stat
    // cards) the click may land on an inner node without the data attributes.
    const el = event.currentTarget
    const indicesRaw = el.dataset.datasetIndices
    const indices = indicesRaw
      ? indicesRaw.split(',').map((s) => parseInt(s.trim(), 10)).filter((n) => !Number.isNaN(n))
      : [parseInt(el.dataset.datasetIndex, 10)]

    const isCheckbox = el.matches('input[type="checkbox"]')
    const visible = isCheckbox ? el.checked : !this.chart.isDatasetVisible(indices[0])

    this.setDatasetsVisibility(indices, visible)

    if (!isCheckbox) {
      el.classList.toggle('opacity-50', !visible)
      el.setAttribute('aria-pressed', visible)
    }

    this.updateSelectAllCheckbox()
  }

  toggleAllDatasets(event) {
    if (!this.chart) return

    const isVisible = event.target.checked
    const allIndices = this.chart.data.datasets.map((_, index) => index)

    this.setDatasetsVisibility(allIndices, isVisible)

    // Update all individual checkboxes
    const checkboxes = this.element.querySelectorAll('.dataset-checkbox')
    checkboxes.forEach((checkbox) => {
      checkbox.checked = isVisible
    })
  }

  setDatasetsVisibility(indices, isVisible) {
    indices.forEach((index) => {
      this.chart.setDatasetVisibility(index, isVisible)
    })
    this.chart.update()
  }

  updateSelectAllCheckbox() {
    const checkboxes = this.element.querySelectorAll('.dataset-checkbox')
    const selectAllCheckbox = this.element.querySelector('#toggle-all-datasets')

    if (!selectAllCheckbox || checkboxes.length === 0) return

    const checkedCount = Array.from(checkboxes).filter((cb) => cb.checked).length

    if (checkedCount === 0) {
      selectAllCheckbox.checked = false
      selectAllCheckbox.indeterminate = false
    } else if (checkedCount === checkboxes.length) {
      selectAllCheckbox.checked = true
      selectAllCheckbox.indeterminate = false
    } else {
      selectAllCheckbox.checked = false
      selectAllCheckbox.indeterminate = true
    }
  }
}
